import { Component, EventEmitter, inject, Output, signal, ViewChild, computed } from '@angular/core';
import { DrawerModule } from 'primeng/drawer';
import { ButtonModule } from 'primeng/button';
import { AvatarModule } from 'primeng/avatar';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { PanelMenuModule } from 'primeng/panelmenu';
import { RippleModule } from 'primeng/ripple';
import { SidebarSettingsComponent } from './sidebar-settings/sidebar-settings';
import { SidebarStore } from './store/sidebar.store';
import { Module } from '../../../../core/services/roles-permissions/types';
import { hasAction } from '../../../../core/utils/has-actions.util';
import { ACTIONS } from '../../../../core/constants/actions.constants';

@Component({
    selector: 'app-sidebar',
    templateUrl: './sidebar.component.html',
    styleUrls: ['./sidebar.component.scss'],
    imports: [DrawerModule, ButtonModule, AvatarModule, CommonModule, PanelMenuModule, RippleModule, SidebarSettingsComponent]
})
export class SidebarComponent {
    @Output() onClickLink = new EventEmitter<void>();
    private readonly sidebarStore = inject(SidebarStore);
    private readonly router = inject(Router);

    mainMenuItems = computed(() => {
        const modules = this.sidebarStore.getMainMenuModules();
        const filteredModules = modules.filter(module =>
            module.url !== 'settings' &&
            module.showInMainMenu
        );
        return this.transformModulesToMenuItems(filteredModules);
    });

    settingsMenuItems = computed(() => {
        const modules = this.sidebarStore.getSettingsModules();
        const filteredSettingsModules = modules.filter(module =>
            module.url !== 'settings'
        );
        const menuItems = this.transformModulesToMenuItems(filteredSettingsModules);

        menuItems.push({
            label: 'Integraciones',
            icon: 'pi pi-link',
            routerLink: '/crm/settings/integrations',
            routerLinkActive: 'active'
        });

        menuItems.push({
            label: 'Registro de Auditoría',
            icon: 'pi pi-history',
            routerLink: '/crm/settings/audit-log',
            routerLinkActive: 'active'
        });

        if (hasAction(ACTIONS.VIEW_RECORDING_HISTORY)) {
            menuItems.push({
                label: 'Histórico de Clases',
                icon: 'pi pi-video',
                routerLink: '/crm/settings/recording-history',
                routerLinkActive: 'active'
            });
        }

        if (hasAction(ACTIONS.EDIT_ROLES_PERMISSIONS)) {
            menuItems.push({
                label: 'Constantes',
                icon: 'pi pi-sliders-h',
                routerLink: '/crm/settings/constants',
                routerLinkActive: 'active'
            });
        }

        if (hasAction(ACTIONS.VIEW_NOTIFICATIONS)) {
            menuItems.push({
                label: 'Notificaciones',
                icon: 'pi pi-bell',
                routerLink: '/crm/settings/notifications',
                routerLinkActive: 'active'
            });
        }

        return menuItems;
    });

    ngOnInit() {
        this.sidebarStore.getSidebarByRoleId();
    }

    isSettingsView(): boolean {
        return this.router.url.includes('/crm/settings');
    }

    isActiveRoute(route: string): boolean {
        return this.router.url === route || this.router.url.startsWith(route + '/');
    }

    private transformModulesToMenuItems(modules: Module[]): any[] {
        return modules.map((module) => {
            const baseItem = {
                label: module.name,
                icon: `pi ${module.icon}`,
                routerLink: module.url ? `/crm/${module.url}` : undefined,
                routerLinkActive: 'active'
            };

            const visibleFunctionalities = module.functionalities.filter(func => func.showInMenu);

            if (!module.url && visibleFunctionalities.length > 0) {
                const subItems = visibleFunctionalities
                    .sort((a, b) => a.order - b.order)
                    .filter(func => {
                        return !!func.url;
                    })
                    .map(func => {
                        return {
                            label: func.name,
                            routerLink: `/crm/${func.url}`,
                            routerLinkActive: 'active'
                        };
                    });

                if (subItems.length > 0) {
                    return {
                        ...baseItem,
                        items: subItems
                    };
                }

                return null;
            } else if (module.url && visibleFunctionalities.length > 0) {
                return {
                    ...baseItem,
                    items: visibleFunctionalities.map(func => {
                        return {
                            label: func.name,
                            routerLink: `/crm/${func.url}`,
                            routerLinkActive: 'active'
                        };
                    })
                };
            }

            return baseItem;
        }).filter((item: any) => item !== null && (!!item.routerLink || (!!item.items && item.items.length > 0)));
    }

}